// assets/tc-fba-calc-core.js
// Pure FBA fee math — no DOM. Loaded before tc-fba-calc.js.
// Annual maintenance: update FBA_RATES only. Sources: Amazon 2026 US non-peak rate
// card (surcharge-excluded columns, $10–$50 price band — typical toys; >$50 items
// run slightly higher in some standard bands, a known simplification). Bulky items
// use the Large Bulky rate even when Small Bulky might apply (Amazon's small-vs-large
// bulky dimension split isn't modeled; this overstates by ≤ ~$1.80/unit).
// Weight rule: fulfillment, returns-processing, and removal fees bill on SHIPPING
// weight — the greater of unit weight and dimensional weight (L×W×H ÷ 139) — for
// large standard, bulky, and extra-large items. Small standard and extra-large
// 150+ lb units bill on unit weight. Dimensional weight for bulky and extra-large
// assumes a 2-inch minimum on the two smaller dimensions. Size-tier assignment and
// storage use actual dimensions/unit weight.
(function () {
  'use strict';

  // Rate figures are ALSO hardcoded in these other places — update them together every year:
  //   - assets/tc-fba-calc.js: UI copy literals "15%", "3.5%", "4.7%" (referral, fuel surcharge, return-fee threshold)
  //   - sections/tc-fba-calculator.liquid: the <noscript> fee table, and the "rates_as_of" setting default
  //   - templates/page.tc-fba-calculator.json: the "rates_as_of" setting value on the "calculator" section,
  //     and the methodology rich-text block's copy (referral %, fuel surcharge %, return-fee threshold %)
  var FBA_RATES = {
    ratesAsOf: 'July 2026',
    referralPct: 0.15,          // Toys & Games, US
    referralMin: 0.30,
    refundAdminCap: 5.00,
    refundAdminPct: 0.20,       // of referral fee
    fuelSurchargePct: 0.035,    // FBA fulfillment fee surcharge from 2026-04-17
    smallStandard: {            // ≤16oz AND ≤15×12×0.75in — flat items only
      maxL: 15, maxW: 12, maxH: 0.75, maxLb: 1,
      // price band $10–$50 (typical toys); [maxOz, fee]
      bands: [[2, 3.32], [4, 3.42], [6, 3.45], [8, 3.54], [10, 3.68], [12, 3.78], [14, 3.91], [16, 3.96]],
    },
    largeStandard: {            // ≤20lb AND ≤18×14×8in
      maxL: 18, maxW: 14, maxH: 8, maxLb: 20,
      bands: [                  // [maxLb, fee]
        [0.25, 3.73], [0.5, 3.95], [0.75, 4.20], [1, 4.60],
        [1.25, 5.04], [1.5, 5.42], [1.75, 5.57], [2, 5.82],
        [2.25, 5.92], [2.5, 6.10], [2.75, 6.26], [3, 6.67],
      ],
      over3: { base: 6.97, perQuarterLb: 0.08 },   // 3–20lb: $6.97 + $0.08 per 4oz over 3lb
    },
    largeBulky: {               // ≤50lb AND ≤59×33×33in
      maxL: 59, maxW: 33, maxH: 33, maxLb: 50,
      base: 9.35, perLbOverFirst: 0.38,
    },
    extraLarge: {               // anything heavier than 50lb, or bigger than large bulky
      xl0to50: { base: 26.33, perLbOverFirst: 0.38 },   // oversized dims, ≤50lb
      xl50to70: { base: 37.32, perLb: 0.75, above: 51 },
      xl70to150: { base: 51.32, perLb: 0.75, above: 71 },
      xl150plus: { base: 194.95, perLb: 0.19, above: 151 },
    },
    storage: {                  // $/cuft/month (base rate, below-22-week utilization)
      standard: { offPeak: 0.78, peak: 2.40 },     // peak = Oct–Dec
      oversize: { offPeak: 0.56, peak: 1.40 },
    },
    removal: {                  // per unit, by shipping weight
      standard: [[0.5, 1.04], [1.0, 1.53], [2.0, 2.27]],
      standardOver2: { base: 2.89, perLbOver2: 1.06 },
      bulky: [[1.0, 3.12], [2.0, 4.30], [4.0, 6.36], [10.0, 10.04]],
      bulkyOver10: { base: 14.32, perLbOver10: 1.06 },
    },
    disposalSameAsRemoval: true,
    returnProcessing: {         // per returned unit, non-apparel, when BOTH gates fail
      thresholdPct: 4.7,        // Toys & Games category threshold
      exemptUnitsPerMonth: 25,
      smallStandard: [           // [maxLb, fee] — 2oz steps
        [0.125, 1.78], [0.25, 1.84], [0.375, 1.90], [0.5, 1.96],
        [0.625, 2.02], [0.75, 2.08], [0.875, 2.14], [1, 2.21],
      ],
      largeStandard: [
        [0.25, 2.36], [0.5, 2.70], [0.75, 3.05], [1, 3.39],
        [1.25, 3.70], [1.5, 4.01], [1.75, 4.32], [2, 4.63],
        [2.25, 4.66], [2.5, 4.68], [2.75, 4.71], [3, 4.73],
      ],
      largeStandardOver3: { base: 5.00, per4ozOver3: 0.05 },
      bulky: { base: 6.74, perLbOverFirst: 0.32 },   // small + large bulky, 0–50lb
      extraLarge: {
        xl0to50: { base: 26.33, perLbOverFirst: 0.38 },
        xl50to70: { base: 40.12, perLb: 0.75, above: 51 },
        xl70to150: { base: 52.45, perLb: 0.75, above: 71 },
        xl150plus: { base: 157.35, perLb: 0.19, above: 151 },
      },
    },
    liquidation: {
      grossRecoveryLowPct: 0.05, grossRecoveryHighPct: 0.10,  // Amazon's estimate
      referralOnRecoveryPct: 0.15,
      processingFee: 1.45,      // derived from Amazon's $109.99 scooter example ($3.22–$7.90 net)
    },
  };

  // Money is whole cents. Every fee below is quantized to cents once, at the point it is
  // computed, and every total is the sum of already-quantized parts — never a sum of raw
  // floats that the UI then rounds independently. That is what keeps the printed receipt
  // adding up: a total summed from unrounded parts can differ from the sum of the rounded
  // parts it sits under (the ride-on preset printed 10.50+16.95+0.59+1.81 under a 29.86 total).
  // Half rounds away from zero, so a credit row rounds by the same magnitude as a charge
  // row: Math.round(-1354.5) would give -1354 while +1354.5 gives 1355, which reads as an
  // error next to the "$3.01 × 450" the row prints beside it.
  function roundTo(n, dp) {
    var f = Math.pow(10, dp);
    return (n < 0 ? -1 : 1) * Math.round(Math.abs(n) * f) / f;
  }
  function cents(n) { return roundTo(n, 2); }

  // Round each value to `dp` decimals, then sum THOSE — for panels that display at a
  // coarser precision than cents (the returns table shows whole dollars). Returns the
  // rounded parts alongside their total so a caller renders both from one call and
  // cannot drift. Callers must print `parts`, not their own rounding of the inputs.
  function roundAndSum(values, dp) {
    var d = dp || 0;
    var parts = values.map(function (v) { return roundTo(v, d); });
    return { parts: parts, total: roundTo(parts.reduce(function (a, b) { return a + b; }, 0), d) };
  }

  function bandLookup(bands, value) {
    for (var i = 0; i < bands.length; i++) if (value <= bands[i][0]) return bands[i][1];
    return null;
  }

  function sizeTierFor(d) {
    var dims = [d.l, d.w, d.h].sort(function (a, b) { return b - a; }); // longest→shortest
    var s = FBA_RATES.smallStandard, ls = FBA_RATES.largeStandard, lb = FBA_RATES.largeBulky;
    if (d.weightLb <= s.maxLb && dims[0] <= s.maxL && dims[1] <= s.maxW && dims[2] <= s.maxH) return 'smallStandard';
    if (d.weightLb <= ls.maxLb && dims[0] <= ls.maxL && dims[1] <= ls.maxW && dims[2] <= ls.maxH) return 'largeStandard';
    if (d.weightLb <= lb.maxLb && dims[0] <= lb.maxL && dims[1] <= lb.maxW && dims[2] <= lb.maxH) return 'largeBulky';
    return 'extraLarge';
  }

  function dimWeightLb(tier, d) {
    var dims = [d.l, d.w, d.h].sort(function (a, b) { return b - a; });
    if (tier === 'largeBulky' || tier === 'extraLarge') {
      // bulky/XL dimensional weight assumes ≥2in on the two smaller dimensions
      return (dims[0] * Math.max(2, dims[1]) * Math.max(2, dims[2])) / 139;
    }
    return (d.l * d.w * d.h) / 139;
  }

  // Shipping weight bills fulfillment, returns-processing, and removal fees.
  // Unit weight applies to small standard and to extra-large units over 150lb.
  function shippingWeightLb(tier, d) {
    if (tier === 'smallStandard') return d.weightLb;
    if (tier === 'extraLarge' && d.weightLb > 150) return d.weightLb;
    return Math.max(d.weightLb, dimWeightLb(tier, d));
  }

  function fulfillmentFee(tier, weightLb) {
    if (tier === 'smallStandard') return bandLookup(FBA_RATES.smallStandard.bands, weightLb * 16);
    if (tier === 'largeStandard') {
      var f = bandLookup(FBA_RATES.largeStandard.bands, weightLb);
      if (f !== null) return f;
      var o = FBA_RATES.largeStandard.over3;
      return o.base + o.perQuarterLb * Math.ceil((weightLb - 3) / 0.25);
    }
    if (tier === 'extraLarge') {
      var x = FBA_RATES.extraLarge;
      if (weightLb <= 50) return x.xl0to50.base + x.xl0to50.perLbOverFirst * Math.max(0, Math.ceil(weightLb) - 1);
      var band = weightLb <= 70 ? x.xl50to70 : (weightLb <= 150 ? x.xl70to150 : x.xl150plus);
      return band.base + band.perLb * Math.max(0, Math.ceil(weightLb) - band.above);
    }
    var b = FBA_RATES.largeBulky;
    return b.base + b.perLbOverFirst * Math.max(0, Math.ceil(weightLb) - 1);
  }

  function removalFee(tier, weightLb) {
    var r = FBA_RATES.removal;
    if (tier === 'largeBulky' || tier === 'extraLarge') {
      var f = bandLookup(r.bulky, weightLb);
      return f !== null ? f : r.bulkyOver10.base + r.bulkyOver10.perLbOver10 * (weightLb - 10);
    }
    var f2 = bandLookup(r.standard, weightLb);
    return f2 !== null ? f2 : r.standardOver2.base + r.standardOver2.perLbOver2 * (weightLb - 2);
  }

  function returnProcessingFee(tier, weightLb) {
    var rp = FBA_RATES.returnProcessing;
    if (tier === 'smallStandard') {
      var sf = bandLookup(rp.smallStandard, weightLb);
      return sf !== null ? sf : rp.smallStandard[rp.smallStandard.length - 1][1];
    }
    if (tier === 'largeStandard') {
      var lf = bandLookup(rp.largeStandard, weightLb);
      if (lf !== null) return lf;
      var o = rp.largeStandardOver3;
      return o.base + o.per4ozOver3 * Math.ceil((weightLb - 3) / 0.25);
    }
    if (tier === 'extraLarge') {
      var x = rp.extraLarge;
      if (weightLb <= 50) return x.xl0to50.base + x.xl0to50.perLbOverFirst * Math.max(0, Math.ceil(weightLb) - 1);
      var band = weightLb <= 70 ? x.xl50to70 : (weightLb <= 150 ? x.xl70to150 : x.xl150plus);
      return band.base + band.perLb * Math.max(0, Math.ceil(weightLb) - band.above);
    }
    return rp.bulky.base + rp.bulky.perLbOverFirst * Math.max(0, Math.ceil(weightLb) - 1);
  }

  function compute(inp) {
    var tier = sizeTierFor(inp);
    var dimWt = dimWeightLb(tier, inp);
    var shipWt = shippingWeightLb(tier, inp);
    var referral = cents(Math.max(FBA_RATES.referralPct * inp.price, FBA_RATES.referralMin));
    var fulfillment = cents(fulfillmentFee(tier, shipWt));
    var fuelSurcharge = cents(fulfillment * FBA_RATES.fuelSurchargePct);
    var cuft = (inp.l * inp.w * inp.h) / 1728;
    var sRates = (tier === 'largeBulky' || tier === 'extraLarge') ? FBA_RATES.storage.oversize : FBA_RATES.storage.standard;
    var months = inp.monthsInStorage;
    var holidayMonths = inp.includesHoliday ? Math.min(3, months) : 0;
    var storagePerUnit = cents(cuft * (sRates.peak * holidayMonths + sRates.offPeak * (months - holidayMonths)));
    var totalFees = cents(referral + fulfillment + fuelSurcharge + storagePerUnit);
    var netProceeds = cents(inp.price - totalFees - inp.unitCost - (inp.inboundPerUnit || 0));
    var marginPct = inp.price > 0 ? (netProceeds / inp.price) * 100 : 0;

    // Returns module
    var rate = inp.returnRatePct / 100;
    var perYear = Math.round(inp.unitsPerMonth * 12 * rate);
    // Whole units — the table prints these counts as multipliers ("$25.00 × 270"), so a
    // fractional count would make the row disagree with its own amount.
    var unsellablePerYear = Math.round(perYear * (inp.unsellablePct / 100));
    var sellablePerYear = perYear - unsellablePerYear;
    var refundAdmin = cents(Math.min(FBA_RATES.refundAdminCap, FBA_RATES.refundAdminPct * referral));
    var feeExempt = inp.returnRatePct <= FBA_RATES.returnProcessing.thresholdPct
      || inp.unitsPerMonth <= FBA_RATES.returnProcessing.exemptUnitsPerMonth;
    var rpf = feeExempt ? 0 : cents(returnProcessingFee(tier, shipWt));

    var liq = FBA_RATES.liquidation;
    function liqNet(pct) {
      var gross = pct * inp.price;
      return gross * (1 - liq.referralOnRecoveryPct) - liq.processingFee;
    }
    var liquidationNetLow = cents(liqNet(liq.grossRecoveryLowPct));
    var liquidationNetHigh = cents(liqNet(liq.grossRecoveryHighPct));
    var liquidationNetMid = cents((liquidationNetLow + liquidationNetHigh) / 2);
    var removal = cents(removalFee(tier, shipWt));
    var disposalNet = cents(-(inp.unitCost + removal)); // disposal fee = removal fee
    var resaleNet = cents((inp.resalePct / 100) * inp.price - removal);

    // Headline: status quo = liquidation midpoint (best Amazon-native option).
    // The four rows below ARE the headline — the UI renders this breakdown and sums the
    // rounded rows via roundAndSum, so the table always adds up to the figure beneath it.
    // Algebraically identical to the old closed form, since sellable + unsellable = perYear:
    //   perYear×(refundAdmin + rpf) + unsellable×unitCost − unsellable×liquidationRecovery
    var liqRecovery = Math.max(liquidationNetMid, 0);
    var costBreakdown = [
      { key: 'refundAdmin', perUnit: refundAdmin, count: perYear, amount: cents(refundAdmin * perYear) },
      { key: 'returnProcessing', perUnit: rpf, count: perYear, amount: cents(rpf * perYear) },
      { key: 'productCostLost', perUnit: inp.unitCost, count: unsellablePerYear, amount: cents(inp.unitCost * unsellablePerYear) },
      { key: 'liquidationRecovery', perUnit: -liqRecovery, count: unsellablePerYear, amount: cents(-liqRecovery * unsellablePerYear) },
    ];
    var recoverable = {
      count: unsellablePerYear, resalePerUnit: resaleNet, liquidationPerUnit: liqRecovery,
      resaleTotal: cents(resaleNet * unsellablePerYear),
      liquidationTotal: cents(liqRecovery * unsellablePerYear),
    };
    // Both annual headlines are WHOLE-DOLLAR figures by definition: they are the sum of the
    // dollar-rounded rows the page shows, not a cent-precise total rounded after the fact.
    var annualCostStatusQuo = roundAndSum(costBreakdown.map(function (c) { return c.amount; }), 0).total;
    var annualRecoverableDelta = Math.max(0, roundAndSum([recoverable.resaleTotal, -recoverable.liquidationTotal], 0).total);

    return {
      tier: tier, shippingWeightLb: shipWt, dimWeightLb: dimWt,
      billedOnDimWeight: shipWt > inp.weightLb + 1e-9,
      referral: referral, fulfillment: fulfillment, fuelSurcharge: fuelSurcharge,
      storagePerUnit: storagePerUnit, totalFees: totalFees, netProceeds: netProceeds, marginPct: marginPct,
      returns: {
        perYear: perYear, sellablePerYear: sellablePerYear, unsellablePerYear: unsellablePerYear,
        refundAdminPerReturn: refundAdmin, returnProcessingPerReturn: rpf, feeExempt: feeExempt,
        disposalNet: disposalNet, liquidationNetLow: liquidationNetLow, liquidationNetHigh: liquidationNetHigh,
        liquidationNetMid: liquidationNetMid, resaleNet: resaleNet,
        costBreakdown: costBreakdown, recoverable: recoverable,
        annualCostStatusQuo: annualCostStatusQuo, annualRecoverableDelta: annualRecoverableDelta,
      },
    };
  }

  var api = { FBA_RATES: FBA_RATES, roundAndSum: roundAndSum, sizeTierFor: sizeTierFor, dimWeightLb: dimWeightLb,
    shippingWeightLb: shippingWeightLb, fulfillmentFee: fulfillmentFee,
    removalFee: removalFee, returnProcessingFee: returnProcessingFee, compute: compute };
  if (typeof module !== 'undefined' && module.exports) module.exports = api;
  if (typeof window !== 'undefined') window.TcFbaCore = api;
})();
